import { Link } from 'react-router-dom';
import { galleryImages } from '../../data/homeImages';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { ArrowRight } from '../ui/Icons';

/**
 * Editorial grid: one tall feature on the left, a stack of smaller frames on the
 * right. `span` drives the desktop layout only — on mobile everything stacks.
 */
const SHOTS = [
  {
    image: galleryImages.interiorLiving,
    title: 'Warm-neutral living room',
    alt: 'Living room painted in a warm neutral Vertex interior shade',
    kind: 'Interior',
    link: '/InteriorWallPaint/Shades',
    span: 'lg:col-span-2 lg:row-span-2',
    height: 'h-72 lg:h-full lg:min-h-[32rem]',
  },
  {
    image: galleryImages.exteriorVilla,
    title: 'Contemporary villa elevation',
    alt: 'Cube-style villa elevation finished in Vertex exterior emulsion',
    kind: 'Exterior',
    link: '/Exterior/CraftedCube',
    span: 'lg:col-span-2',
    height: 'h-56 lg:h-[15.5rem]',
  },
  {
    image: galleryImages.interiorBedroom,
    title: 'Soft-tone bedroom',
    alt: 'Bedroom walls painted in a soft Vertex interior tone',
    kind: 'Interior',
    link: '/InteriorWallPaint/Shades',
    span: '',
    height: 'h-56 lg:h-[15.5rem]',
  },
  {
    image: galleryImages.interiorLounge,
    title: 'Textured feature wall',
    alt: 'Lounge with a textured feature wall finished by Vertex',
    kind: 'Texture',
    link: '/textures',
    span: '',
    height: 'h-56 lg:h-[15.5rem]',
  },
];

const Inspiration = () => (
  <section className="vp-section bg-brand-surface">
    <div className="container">
      <SectionHeading
        eyebrow="Inspiration"
        title="Spaces finished in Vertex"
        lead="Real interiors and elevations, painted with our products. Use them as a starting point for your own scheme."
        className="mb-10"
        action={
          <Link to="/visualizer" className="vp-btn vp-btn-outline">
            Try the visualiser
            <ArrowRight size={16} />
          </Link>
        }
      />

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        {SHOTS.map((s, i) => (
          <Reveal as="li" key={s.title} delay={Math.min(i, 4) as 0 | 1 | 2 | 3 | 4} className={s.span}>
            <Link to={s.link} className={`group relative block w-full rounded-2xl overflow-hidden bg-brand-navy ${s.height}`}>
              <img
                src={s.image}
                alt={s.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-premium group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e1330]/85 via-[#0e1330]/15 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 flex items-end justify-between gap-4">
                <div className="min-w-0">
                  <span className="vp-chip vp-chip-light text-[11px] uppercase tracking-wider">{s.kind}</span>
                  <h3 className="mt-2.5 text-lg sm:text-xl font-semibold text-white leading-snug">{s.title}</h3>
                </div>
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-white/15 border border-white/25 text-white flex items-center justify-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-premium">
                  <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export default Inspiration;
